import { type ReactNode, type Ref } from "react";

import { cn } from "../../utils";
import { Tooltip } from "../Tooltip";

export const radioSizes = ["xs", "sm", "md", "lg", "xl"] as const;
export const radioVariants = ["primary", "secondary", "tertiary"] as const;

export type RadioSize = (typeof radioSizes)[number];
export type RadioVariant = (typeof radioVariants)[number];

export interface RadioItem<T extends string | number = string> {
  id: T;
  label: ReactNode;
  tooltip?: ReactNode;
  disabled?: boolean;
  className?: string | ((isActive: boolean) => string);
}

export interface RadioProps<T extends string | number = string> {
  ref?: Ref<HTMLDivElement>;
  value: T;
  size?: RadioSize;
  variant?: RadioVariant;
  items: RadioItem<T>[];
  className?: string;
  onChange?: (id: T) => void;
}

export const RadioButton = <T extends string | number = string>({
  ref,
  value,
  size = "lg",
  variant = "primary",
  items,
  className,
  onChange,
  ...props
}: RadioProps<T>) => {
  const isPrimary = variant === "primary";
  const isSecondary = variant === "secondary";
  const isTertiary = variant === "tertiary";

  return (
    <div
      ref={ref}
      className={cn(
        "ui:flex ui:items-center ui:text-textPrimary",
        { "ui:gap-2": !isTertiary },
        className,
      )}
      {...props}
    >
      {items.map((item) => {
        const isActive = item.id === value;
        return (
          <Tooltip
            key={item.id}
            content={item.tooltip}
            disabled={!item.tooltip}
          >
            <button
              disabled={item.disabled}
              className={cn(
                "ui:cursor-pointer ui:border ui:px-2 ui:text-xs ui:transition-colors",
                {
                  "ui:cursor-not-allowed ui:opacity-20": item.disabled,
                  "ui:hover:bg-buttonHover": !item.disabled && !isActive,
                  "ui:cursor-default": isActive,
                },
                {
                  "ui:rounded-[3px] ui:font-semibold": isPrimary,
                  "ui:border-primary ui:bg-primary/10": isPrimary && isActive,
                  "ui:border-transparent ui:bg-buttonBg ui:dark:bg-buttonFill":
                    isPrimary && !isActive,
                },
                {
                  "ui:rounded-[3px] ui:font-normal": isSecondary,
                  "ui:border-primary ui:bg-primary/10 ui:font-bold":
                    isSecondary && isActive,
                  "ui:border-divider ui:bg-buttonBg ui:font-normal ui:text-textSecondary":
                    isSecondary && !isActive,
                },
                {
                  "ui:border-r-0 ui:border-divider ui:first-of-type:rounded-l-sm ui:last-of-type:rounded-r-sm ui:last-of-type:border-r":
                    isTertiary,
                  "ui:font-normal ui:text-textSecondary":
                    isTertiary && !isActive,
                  "ui:font-bold": isTertiary && isActive,
                },
                {
                  "ui:h-5.5 ui:min-h-5.5": size === "xs",
                  "ui:h-6 ui:min-h-6": size === "sm",
                  "ui:h-7 ui:min-h-7": size === "md",
                  "ui:h-8 ui:min-h-8": size === "lg",
                  "ui:h-10 ui:min-h-10": size === "xl",
                },
                typeof item.className === "string"
                  ? item.className
                  : item.className?.(isActive),
              )}
              onClick={() => {
                if (!item.disabled) {
                  onChange?.(item.id);
                }
              }}
            >
              {item.label}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
